import { FormConfig, FormValues, NextFunction } from '@topdanmark-onlinesolutions/claim-form-types';
import { getValueOrDefault } from '../components/values/Get';
import { getTodaysDateDKFormattedWithDashes } from '../helpers/dates';

enum ReasonForCancellation {
    NotSpecified,
    PersonalIllness,
    IllnessWithinHousehold,
    IllnessOutsideHousehold,
    Divorce,
    Other
}

export const getNextPage: NextFunction = async (curPageId: string, formConfig: FormConfig, formValues: FormValues) => {
    let nextPage: string | undefined = '';

    switch (curPageId) {
        // =============================================================
        // Step 0: Landing page
        // =============================================================
        case 'godt-at-vide':
            nextPage = 'afbestilling-aarsag';
            break;

        // =============================================================
        // Step 1: About the cancellation
        // =============================================================
        case 'afbestilling-aarsag':
            nextPage = 'aarsag-til-afbestilling';

            switch (getReasonForCancellation(formConfig, formValues)) {
                case ReasonForCancellation.PersonalIllness:
                case ReasonForCancellation.IllnessWithinHousehold:
                case ReasonForCancellation.IllnessOutsideHousehold:
                    nextPage = 'beskriv-symptomer';
                    break;
                case ReasonForCancellation.Divorce:
                    nextPage = 'om-samliv';
                    break;
                case ReasonForCancellation.Other:
                    nextPage = 'aarsag-til-afbestilling';
                    break;
            }

            break;
        case 'beskriv-symptomer':
            nextPage = undefined;

            switch (getReasonForCancellation(formConfig, formValues)) {
                case ReasonForCancellation.PersonalIllness:
                    nextPage = 'oplysninger-sygdom-tilskadekomst';
                    break;
                case ReasonForCancellation.IllnessWithinHousehold:
                    nextPage = 'oplysninger-sygdom-tilskadekomst-2';
                    break;
                case ReasonForCancellation.IllnessOutsideHousehold:
                    nextPage = 'oplysninger-sygdom-tilskadekomst-3';
                    break;
            }

            break;
        case 'oplysninger-sygdom-tilskadekomst':
            nextPage = 'rejsemaal';
            break;
        // Andres sygdom indenfor husstanden (illness withing household)
        case 'oplysninger-sygdom-tilskadekomst-2':
            nextPage = 'om-personen';
            break;
        // Andres sygdom udenfor husstanden (illness outside household)
        case 'oplysninger-sygdom-tilskadekomst-3':
            nextPage = 'om-personen';
            break;
        case 'om-personen':
            nextPage = undefined;

            const answerWasPersonGoingToJoinTheTrip = getValueOrDefault(
                formConfig,
                formValues,
                'wasPersonGoingToJoinTheTrip'
            ).value;

            if (answerWasPersonGoingToJoinTheTrip === 'Ja') {
                nextPage = 'eneste-rejseledsager';
            } else {
                nextPage = 'relation';
            }

            break;
        case 'eneste-rejseledsager':
            nextPage = 'rejsemaal';

            const isOnlyTravelCompanion = getValueOrDefault(
                formConfig,
                formValues,
                'wasPersonYourOnlyTravelCompanion'
            ).value;

            if (isOnlyTravelCompanion === 'Nej') {
                nextPage = 'relation';
            }

            break;
        case 'relation':
            nextPage = 'rejsemaal';
            break;
        case 'om-samliv':
            nextPage = 'rejsemaal';

            // a dateOfLoss field is mandatory in the CA backend, but the inputfield is not shown to customer when cancellation reason 'other' or 'divorce' is selected
            formValues['dateOfLoss5'] = getTodaysDateDKFormattedWithDashes();

            break;
        case 'aarsag-til-afbestilling':
            nextPage = 'rejsemaal';

            // a dateOfLoss field is mandatory in the CA backend, but the inputfield is not shown to customer when cancellation reason 'other' or 'divorce' is selected
            formValues['dateOfLoss4'] = getTodaysDateDKFormattedWithDashes();

            break;

        // =============================================================
        // Step 2: About the trip
        // =============================================================
        case 'rejsemaal':
            nextPage = 'rejsedatoer';
            break;
        case 'rejsedatoer':
            nextPage = 'personer-i-feriebolig';
            break;
        case 'personer-i-feriebolig':
            nextPage = 'rejsens-formaal';
            break;
        case 'rejsens-formaal':
            const travelPurpose = getValueOrDefault(formConfig, formValues, 'travelPurpose').value;

            if (travelPurpose === 'vacation') {
                nextPage = 'rejseselskab';
            } else {
                nextPage = 'om-erstatning';
            }

            break;
        case 'rejseselskab':
            nextPage = 'om-erstatning';
            break;

        // =============================================================
        // Step 3: About expenses
        // =============================================================
        case 'om-erstatning':
            nextPage = 'er-noget-afbestilt';
            break;
        case 'er-noget-afbestilt':
            nextPage =
                getValueOrDefault(formConfig, formValues, 'hasAnyPartsAlreadyBeenCancelled').value === 'Ja'
                    ? 'hvad-er-refunderet'
                    : 'samlet-refundering';

            break;
        case 'hvad-er-refunderet':
            nextPage = 'samlet-refundering';
            break;
        case 'samlet-refundering':
            nextPage = 'dokumentation';
            break;
        // =============================================================
        // Step 4: Documentation
        // =============================================================
        case 'dokumentation':
            nextPage = 'forsikringsmaegler';
            break;

        // =============================================================
        // Step 5: About the company
        // =============================================================
        case 'forsikringsmaegler':
            nextPage = 'anden-rejseforsikring';
            break;

        // =============================================================
        // Step 6: About you
        // =============================================================
        case 'anden-rejseforsikring':
            nextPage =
                getValueOrDefault(formConfig, formValues, 'doYouHaveAnyOtherTravelInsurance').value === 'Ja'
                    ? 'hvilket-selskab'
                    : 'om-dig';
            break;
        case 'hvilket-selskab':
            nextPage = 'om-dig';
            break;
        case 'om-dig':
            nextPage = 'resume';
            break;
        // =============================================================
        // Step 7: Summery
        // =============================================================
        case 'resume':
            nextPage = undefined;
            break;
        default:
            console.log('# curPageId=', curPageId);
            throw new Error('Unknown page: ' + curPageId);
    }

    return nextPage;
};

const getReasonForCancellation = (formConfig: FormConfig, formValues: FormValues): ReasonForCancellation => {
    const reason = getValueOrDefault(formConfig, formValues, 'cancellationReason').value;

    switch (reason) {
        case 'personalIllness':
            return ReasonForCancellation.PersonalIllness;
        case 'illnessWithinHousehold':
            return ReasonForCancellation.IllnessWithinHousehold;
        case 'illnessOutsideHousehold':
            return ReasonForCancellation.IllnessOutsideHousehold;
        case 'divorce':
            return ReasonForCancellation.Divorce;
        case 'other':
            return ReasonForCancellation.Other;
    }

    return ReasonForCancellation.NotSpecified;
};
